// App.js - 使用 routes.js 统一管理
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { FiLogOut } from 'react-icons/fi';

// 导入统一路由配置
import {
  pageToPath,
  permissionCodes,
  getPermissionsMenu,
  setPermissionsMenu,
  hasPermission,
  getFilteredNavItems,
  getDefaultHomePage,
  getNextAvailablePage,
  getRouteFromItem,
  handleNavClick,
  getPathByPageName,
  getPageNameByPath
} from './config/routes';

// 导入页面组件
import LoginPage from './pages/login/LoginPage';
import CategoriesPage from './pages/categories/CategoriesPage';
import HomePage from './pages/home/HomePage';
import MembersPage from './pages/members/MembersPage';
import PromotionPage from './pages/promotion/PromotionPage';
import CouponPage from './pages/coupon/CouponPage';
import BannerPage from './pages/banner/BannerPage';
import DevicePage from './pages/device/DevicePage';
import ProductPage from './pages/product/ProductPage';
import SalesPage from './pages/sales/SalesPage';
import ProductForm from './pages/product/ProductForm';
import ProductEditForm from './pages/product/ProductEditForm';
import ConfigurableProductForm from './pages/product/ConfigurableProductForm';
import ConfigurableProductEditForm from './pages/product/ConfigurableProductEditForm';
import BundleProductForm from './pages/product/BundleProductForm';
import BundleProductEditForm from './pages/product/BundleProductEditForm';
import AdminPage from './pages/admin/AdminPage';
import SettingsPage from './pages/settings/SettingsPage';
import RestaurantPage from './pages/restaurant/RestaurantsPage';
import RoleManagement from './pages/role/RoleManagement';
import PermissionManagement from './pages/permission/PermissionManagement';

// 导入上下文
import { RestaurantProvider, useRestaurant } from './context/RestaurantContext';
import { FeedbackProvider } from './context/FeedbackContext';

// 导入权限组件
import HasPermi from './components/HasPermi';

import './App.css';

// ========== 全局组件注册 ==========
// 将 HasPermi 挂载到全局 window 对象，这样在任何组件中都可以直接使用 <HasPermi /> 而无需导入
if (typeof window !== 'undefined') {
  window.HasPermi = HasPermi;
}

// 同时挂载到 React 的全局上下文中（可选，用于更严格的类型检查）
if (typeof global !== 'undefined') {
  global.HasPermi = HasPermi;
}

// 导出供其他模块使用（如果需要）
export { HasPermi };
// =================================

// 餐厅选择器组件
function RestaurantSelector() {
  const { restaurants, selectedRestaurant, setSelectedRestaurant } = useRestaurant() || {};
  const isEmpty = !restaurants || restaurants.length === 0;

  return (
    <div className="restaurant-selector">
      <select
        value={selectedRestaurant?.reference || ''}
        disabled={isEmpty}
        onChange={e => {
          const r = restaurants.find(r => r.reference === e.target.value);
          if (r) setSelectedRestaurant(r);
        }}
      >
        {isEmpty
          ? <option value="">No Restaurant</option>
          : restaurants.map(r => (
              <option key={r.reference} value={r.reference}>{r.tenantName}</option>
            ))
        }
      </select>
    </div>
  );
}

// Edit form wrappers
function ProductEditWrapper({ onNavigate }) {
  const location = useLocation();
  return <ProductEditForm onNavigate={onNavigate} product={location.state?.data} />;
}

function ConfigurableProductEditWrapper({ onNavigate }) {
  const location = useLocation();
  return <ConfigurableProductEditForm onNavigate={onNavigate} product={location.state?.data} />;
}

function BundleProductEditWrapper({ onNavigate }) {
  const location = useLocation();
  return <BundleProductEditForm onNavigate={onNavigate} product={location.state?.data} />;
}

function AppRoutes() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!localStorage.getItem('authToken')
  );
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // 加载权限
  useEffect(() => {
    const loadPermissions = () => {
      try {
        const permissions = getPermissionsMenu();
        console.log('AppRoutes - 加载的权限:', permissions);
        setPermissionsLoaded(true);
      } catch (error) {
        console.error('加载权限失败:', error);
      }
    };

    loadPermissions();
  }, []);

  // 增强的导航函数，使用 routes.js 中的函数
  const handleNavigate = (page, data = null) => {
    console.log(`导航请求: ${page}`);

    // 如果权限未加载完成，等待一下
    if (!permissionsLoaded) {
      console.log('权限未加载完成，等待...');
      setTimeout(() => handleNavigate(page, data), 100);
      return;
    }

    // 获取路径
    let path;
    if (page.includes('/')) {
      // 如果已经是路径
      path = page;
    } else {
      // 如果是页面名称，转换为路径
      path = getPathByPageName(page);
    }

    // 检查权限 - 使用页面名称检查
    const pageName = getPageNameByPath(path) || page;
    if (!hasPermission(pageName)) {
      console.warn(`没有权限访问: ${pageName}`);

      // 找到下一个有权限的页面
      const nextAvailablePage = getNextAvailablePage(pageName);
      console.log(`跳转到下一个可用页面: ${nextAvailablePage}`);

      const nextPath = getPathByPageName(nextAvailablePage);
      console.log(`最终路径: ${nextPath}`);

      navigate(nextPath, { state: { data } });
      return;
    }

    // 正常导航
    console.log(`正常导航到: ${path}`);
    navigate(path, { state: { data } });
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    // 登录成功后，根据权限跳转到默认页面
    const defaultPath = getDefaultHomePage();
    navigate(defaultPath);
  };

  const handleLogout = () => {
    // localStorage.removeItem('authToken');
    localStorage.clear();
    
    setIsAuthenticated(false);
    navigate('/login');
  };

  // 如果未认证，显示登录页面
  if (!isAuthenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // 如果权限未加载完成，显示加载状态
  if (!permissionsLoaded) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>加载权限中...</p>
      </div>
    );
  }

  return (
    <RestaurantProvider>
      <div className="app-root">
        <Routes>
          {/* 默认路由 - 根据权限跳转 */}
          <Route path="/" element={<Navigate to={getDefaultHomePage()} replace />} />

          {/* 主要页面路由 */}
          <Route path="/home" element={<HomePage onNavigate={handleNavigate} />} />
          <Route path="/categories" element={<CategoriesPage onNavigate={handleNavigate} />} />
          <Route path="/members" element={<MembersPage onNavigate={handleNavigate} />} />
          <Route path="/promotion" element={<PromotionPage onNavigate={handleNavigate} />} />
          <Route path="/coupon" element={<CouponPage onNavigate={handleNavigate} />} />
          <Route path="/banner" element={<BannerPage onNavigate={handleNavigate} />} />
          <Route path="/device" element={<DevicePage onNavigate={handleNavigate} />} />
          <Route path="/product" element={<ProductPage onNavigate={handleNavigate} />} />
          <Route path="/product/new" element={<ProductForm onNavigate={handleNavigate} />} />
          <Route path="/product/edit" element={<ProductEditWrapper onNavigate={handleNavigate} />} />
          <Route path="/product/new/configurable" element={<ConfigurableProductForm onNavigate={handleNavigate} />} />
          <Route path="/product/edit/configurable" element={<ConfigurableProductEditWrapper onNavigate={handleNavigate} />} />
          <Route path="/product/new/bundle" element={<BundleProductForm onNavigate={handleNavigate} />} />
          <Route path="/product/edit/bundle" element={<BundleProductEditWrapper onNavigate={handleNavigate} />} />
          <Route path="/sales" element={<SalesPage onNavigate={handleNavigate} />} />
          <Route path="/roles" element={<RoleManagement onNavigate={handleNavigate} />} />
          <Route path="/permissions" element={<PermissionManagement onNavigate={handleNavigate} />} />
          <Route path="/settings" element={<Navigate to="/settings/printer-management" replace />} />
          <Route path="/settings/:tab" element={<SettingsPage onNavigate={handleNavigate} />} />
          <Route path="/users" element={<AdminPage onNavigate={handleNavigate} onLogout={handleLogout} />} />
          <Route path="/restaurants" element={<RestaurantPage onNavigate={handleNavigate} />} />

          {/* 404 路由 */}
          <Route path="*" element={<Navigate to={getDefaultHomePage()} replace />} />
        </Routes>
        <RestaurantSelector />
        <button className="logout-btn" onClick={handleLogout}>
          <FiLogOut className="logout-icon" /> Log Out
        </button>
        <footer className="footer">
          广州点线圆科技有限公司
        </footer>
      </div>
    </RestaurantProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <FeedbackProvider>
        <AppRoutes />
      </FeedbackProvider>
    </BrowserRouter>
  );
}

export default App;